import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api.js';

/**
 * ehr#298 - a stopgap until a platform admin page exists: Credify support opens
 * ehr.credifyfast.com/telnyx/<org-id>/<number> to set that org's Telnyx number. The page reads
 * the org and shows the change; nothing saves until Set is pressed (a link alone never writes).
 * The API allows only the support@credifyfast.com sign-in.
 */

interface TelnyxRow {
  orgId: string;
  orgName: string;
  phoneNumber: string;
}

const PATH = /^\/telnyx\/([^/]+)\/([^/]+)\/?$/;

/** The org id and number in /telnyx/<org-id>/<number>, or null on any other path. */
export function readTelnyxPath(
  pathname = window.location.pathname,
): { orgId: string; number: string } | null {
  const m = PATH.exec(pathname);
  if (!m) return null;
  try {
    return { orgId: decodeURIComponent(m[1]!), number: decodeURIComponent(m[2]!) };
  } catch {
    return null;
  }
}

/** Mirrors the API's normalizer so the page shows what will be saved: +15551234567 or null. */
export function previewE164(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  const digits = s.replace(/\D/g, '');
  if (s.startsWith('+')) return /^[1-9]\d{7,14}$/.test(digits) ? `+${digits}` : null;
  if (/^\d{10}$/.test(digits)) return `+1${digits}`;
  if (/^1\d{10}$/.test(digits)) return `+${digits}`;
  return null;
}

export function SupportTelnyxPage({ orgId, number }: { orgId: string; number: string }) {
  const [row, setRow] = useState<TelnyxRow | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const next = previewE164(number);

  useEffect(() => {
    api
      .get<TelnyxRow>(`/support/telnyx/${encodeURIComponent(orgId)}`)
      .then(setRow)
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : 'Could not load that organization.'),
      );
  }, [orgId]);

  const save = () => {
    if (!next) return;
    setSaving(true);
    api
      .put<TelnyxRow>(`/support/telnyx/${encodeURIComponent(orgId)}`, { phoneNumber: next })
      .then((r) => {
        setRow(r);
        setSaved(true);
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : 'Could not save the number.'),
      )
      .finally(() => setSaving(false));
  };

  return (
    <main className="content">
      <div className="card support-telnyx">
        <h2>Set a Telnyx number</h2>
        <p className="muted">
          Credify support only. This sets the organization&apos;s Telnyx phone number (Data &amp;
          Settings → Carrier accounts).
        </p>
        {error ? (
          <p className="support-telnyx-error" role="alert">
            {error}
          </p>
        ) : !row ? (
          <p className="muted">Loading…</p>
        ) : (
          <>
            <dl className="support-telnyx-dl">
              <dt>Organization</dt>
              <dd>
                {row.orgName} <span className="muted">({row.orgId})</span>
              </dd>
              <dt>{saved ? 'Telnyx number' : 'Current number'}</dt>
              <dd>{row.phoneNumber || <span className="muted">None set</span>}</dd>
              {!saved && (
                <>
                  <dt>New number</dt>
                  <dd>
                    {next ?? (
                      <span className="support-telnyx-error">
                        &ldquo;{number}&rdquo; isn&apos;t a phone number. Use 10 digits or a full
                        +country number.
                      </span>
                    )}
                  </dd>
                </>
              )}
            </dl>
            {saved ? (
              <p className="support-telnyx-ok" role="status">
                Saved. {row.orgName}&apos;s Telnyx number is {row.phoneNumber}.
              </p>
            ) : (
              <button
                type="button"
                className="btn primary"
                disabled={!next || saving || next === row.phoneNumber}
                onClick={save}
              >
                {saving
                  ? 'Saving…'
                  : next && next === row.phoneNumber
                    ? 'Already set'
                    : 'Set Telnyx number'}
              </button>
            )}
          </>
        )}
      </div>
    </main>
  );
}
